import {
  computed,
  effect,
  inject,
  Injectable,
  Signal,
  untracked,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { map } from "rxjs";
import {
  FlatEnrichmentModel,
  isFlatEnrichmentModel,
} from "@app/shared/mappers/enrichment-detail.adapter";
import { EnrichmentPriceItem } from "@app/shared/models/enrichment-detail.model";
import { LanguageModeService } from "@app/shared/services/language-mode.service";
import { logger } from "@core/utils/logger";
import { EnrichmentDataService } from "../../../services/enrichment-data.service";
import { editPackageCommands } from "../../../services/edit-package-url";
import { buildPriceEntries, PriceEntry } from "../../../utils/price-entry.util";
import { scopeModelToPrice } from "../../../utils/price-scope.util";

export type ActivePriceState =
  | { readonly kind: "loading" }
  | { readonly kind: "no-prices" }
  | { readonly kind: "redirect"; readonly popId: string }
  | { readonly kind: "not-on-package"; readonly requested: string }
  | { readonly kind: "ready"; readonly popId: string };

/**
 * The price being edited, derived from the route's `:popId` and the loaded
 * package. The router is the only writer: nothing here is set, so the store
 * cannot disagree with the URL. Provided by the edit-package shell so it
 * reads the shell's own `ActivatedRoute` and dies with the route.
 */
@Injectable()
export class ActivePriceStore {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly data = inject(EnrichmentDataService);
  private readonly languageMode = inject(LanguageModeService);

  readonly packageId: Signal<string> = toSignal(
    this.route.paramMap.pipe(map((params) => params.get("id") ?? "")),
    { requireSync: true },
  );

  private readonly requested: Signal<string | null> = toSignal(
    this.route.paramMap.pipe(map((params) => params.get("popId"))),
    { requireSync: true },
  );

  private readonly model = toSignal(this.data.enrichment$, {
    initialValue: null,
  });

  /** The loaded package, once it is the package the route names. The root
   *  store keeps the previous package's model until the shell nulls it at
   *  load start; the id check refuses to resolve a route against it. */
  private readonly flat = computed<FlatEnrichmentModel | null>(() => {
    const model = this.model();
    return model &&
      isFlatEnrichmentModel(model) &&
      model.__detail.id === this.packageId()
      ? model
      : null;
  });

  readonly prices: Signal<readonly EnrichmentPriceItem[]> = computed(
    () => this.flat()?.__detail.prices ?? [],
  );

  readonly entries: Signal<readonly PriceEntry[]> = computed(() =>
    buildPriceEntries(
      this.prices(),
      (value) => this.languageMode.resolveForView(value),
      this.languageMode.activeLocale(),
    ),
  );

  readonly state: Signal<ActivePriceState> = computed(() => {
    if (!this.flat()) return { kind: "loading" };
    const entries = this.entries();
    if (entries.length === 0) return { kind: "no-prices" };
    const requested = this.requested();
    if (requested === null) return { kind: "redirect", popId: entries[0].id };
    if (!entries.some((entry) => entry.id === requested)) {
      return { kind: "not-on-package", requested };
    }
    return { kind: "ready", popId: requested };
  });

  readonly popId: Signal<string | null> = computed(() => {
    const state = this.state();
    return state.kind === "ready" ? state.popId : null;
  });

  readonly price: Signal<EnrichmentPriceItem | null> = computed(() => {
    const id = this.popId();
    if (!id) return null;
    return this.prices().find((p) => p.id === id) ?? null;
  });

  readonly entry: Signal<PriceEntry | null> = computed(() => {
    const id = this.popId();
    return id ? (this.entries().find((e) => e.id === id) ?? null) : null;
  });

  readonly scoped: Signal<FlatEnrichmentModel | null> = computed(() => {
    const model = this.flat();
    if (!model) return null;
    const id = this.popId();
    if (id) return scopeModelToPrice(model, id);
    return this.state().kind === "no-prices" ? model : null;
  });

  constructor() {
    // Nothing can be dirty on a bare URL, so the redirect needs no prompt. A
    // failed navigation changes no signal this effect reads, so it cannot
    // loop; the shell keeps showing its spinner.
    effect(() => {
      const state = this.state();
      if (state.kind !== "redirect") return;
      untracked(() => void this.goTo(state.popId));
    });
  }

  /**
   * Move the editor to a sibling price. Navigate only: the route guard owns
   * the unsaved-edits prompt. `replaceUrl` keeps one editor session one
   * history entry, so Cancel and the browser's Back still reach the list.
   * Resolves true once the switch happened, false when it did not.
   */
  goTo(popId: string): Promise<boolean> {
    if (popId === this.popId()) return Promise.resolve(true);
    return this.router
      .navigate(editPackageCommands(this.packageId(), popId), {
        queryParamsHandling: "preserve",
        replaceUrl: true,
      })
      .catch((err: unknown) => {
        logger.error("[active-price] could not open the price:", err);
        return false;
      });
  }
}
